/**
 * helper functions for podcast duplicate detection and warning management
 */

/**
 * updates duplicate warnings for a specific page's podcast list
 * only marks subsequent occurrences of the same URL as duplicates (keeps first occurrence clean)
 */
export function updatePodcastDuplicateWarnings(
  currentWarnings: Record<string, true>,
  podcastUrls: string[],
  pageIndex: number
): Record<string, true> {
  const updatedWarnings = { ...currentWarnings };

  // clear all warnings for this page
  clearWarningsForPage(updatedWarnings, pageIndex);

  // find and mark duplicates
  const duplicateIndices = findDuplicateIndices(podcastUrls);
  markDuplicateWarnings(updatedWarnings, duplicateIndices, pageIndex);

  return updatedWarnings;
}

/**
 * clears all duplicate warnings for a specific page
 */
export function clearWarningsForPage(warnings: Record<string, true>, pageIndex: number): void {
  Object.keys(warnings).forEach(key => {
    if (key.startsWith(`${pageIndex}-`)) {
      delete warnings[key];
    }
  });
}

/**
 * finds indices of duplicate URLs (excluding first occurrence)
 * we only mark subsequent duplicates to allow the original URL to remain valid
 * this provides better UX where the first instance is considered the "original"
 */
export function findDuplicateIndices(urls: string[]): number[] {
  const urlCounts: { [url: string]: number[] } = {};

  // group indices by URL
  urls.forEach((url, index) => {
    if (url.trim() !== '') {
      if (!urlCounts[url]) {
        urlCounts[url] = [];
      }
      urlCounts[url].push(index);
    }
  });

  // collect indices that are duplicates (skip first occurrence)
  const duplicateIndices: number[] = [];
  Object.values(urlCounts).forEach(indices => {
    if (indices.length > 1) {
      duplicateIndices.push(...indices.slice(1));
    }
  });

  return duplicateIndices;
}

/**
 * marks specific indices as having duplicate warnings
 */
export function markDuplicateWarnings(
  warnings: Record<string, true>,
  duplicateIndices: number[],
  pageIndex: number
): void {
  duplicateIndices.forEach(index => {
    warnings[`${pageIndex}-${index}`] = true;
  });
}
